/**
 * ENG-10367 — release the kanban cards a manager drain is about to orphan.
 *
 * THE DEFECT. `stopPolling()` kills every `agt-*` tmux session
 * (`killAllAgtTmuxSessions`). Any agent mid-turn on an `in_progress` card loses
 * that turn, and nothing releases the card. Measured on integritylabs-2:
 *
 *     07:03:24.000Z  self-update: agt CLI 0.28.877 -> 0.28.883
 *     07:03:58.917Z  "Restarting manager to load upgraded binary"
 *     07:03:59.784Z  [drain] step=sigterm-claude-pids note="no in-flight claude -p children"
 *     07:03:59.846Z  "Killed tmux session 'agt-bob'"
 *
 * bob was mid-turn on card 19a7a7c3 at 07:03:59.846Z. The drain looked for
 * in-flight work, found none of the ONE class it enumerates, and killed the
 * session 62ms later.
 *
 * WHY NO REAPER RECOVERS THIS, which is the part the ticket understates. Both
 * stale reapers share `livenessDefersReap` (packages/api/src/lib/kanban-stale-reap.ts),
 * deliberately, per ENG-7767 — "two triggers with two verdicts is the defect".
 * It declines the reap whenever `agents.last_known_good_at` is fresh:
 *
 *   - route  /host/kanban/reap-stale  -> { ok: true, updated: 0, reason: 'agent_alive' }
 *   - cron   kanban-stale-item-reaper -> same predicate, same verdict
 *
 * After a self-update the agent comes straight back HEALTHY, so
 * `last_known_good_at` is fresh on every subsequent sweep and BOTH reapers
 * decline on every round, indefinitely. The card is not reaped late; on a
 * healthy agent it is unreachable by every automatic recovery path we have.
 * That is why bob's card never came back and why its scheduled task — the PR
 * unblock sweep — stayed jammed for three hours with every signal green.
 *
 * The liveness guard is CORRECT for its own purpose. It is blind to exactly one
 * case: the agent is alive AND its work is about to be destroyed. The drain is
 * the only place that holds both halves of that fact, which is why the release
 * has to happen here and cannot be delegated to either reaper.
 *
 * WHY RELEASE RATHER THAN WAIT FOR THE TURN. ENG-9297 (Done) is
 * "the AUTOMATIC agt self-update starves forever on a busy host": the busy gate
 * is true whenever any agent has a live session, and the hosts this bug hits run
 * 5-6 permanent sessions that never idle, so two hosts sat ~9 releases behind
 * for four days. Its shipped remedy is a wall-clock ceiling that BYPASSES the
 * busy gate. A wait-for-idle at the drain rebuilds that hole from the other
 * side. The documented worst case is dwight (update-window-gate.ts): one restart
 * deferred "roughly 800 times at ~30s intervals, for 7.7 hours, unbroken".
 *
 * (For the record, since the ticket implies otherwise: waiting would NOT trip
 * the ENG-5441 restart breaker. `RestartReason` has no 'self-update' member,
 * the self-update teardown never calls `recordRestartForBreaker`, and a
 * deferred restart records nothing. The argument against waiting is starvation
 * alone.)
 *
 * Pure module — no I/O, no network, no process access. The manager injects the
 * POST and the clock at the seam, so every decision here is unit-testable.
 */

/** One agent whose live session is about to be killed, resolved to an id. */
export interface DrainReleaseTarget {
  codeName: string;
  agentId: string;
}

export interface DrainReleaseTargets {
  /** Live agents we can name to the API. */
  targets: DrainReleaseTarget[];
  /**
   * Live agents whose `agent_id` we could not resolve. These are NOT releasable
   * and must not be silently counted as "nothing to do" — that is the same
   * false all-clear this ticket is about, one level down. Surfaced so the drain
   * line can report them.
   */
  unresolved: string[];
}

/**
 * Partition the live persistent sessions into releasable and unresolvable.
 *
 * `liveAgents` is `agentState.persistentSessionAgents` — membership only, no
 * per-agent busy bit, which is deliberate: we do not need to know whether a
 * turn is running. The session is about to be killed either way, so any
 * `in_progress` card it holds is about to be orphaned either way. Asking "is it
 * mid-turn?" would add a heuristic (pane mtime, transcript age) that can only
 * produce false negatives, and a false negative here is a lost card.
 */
export function decideDrainReleaseTargets(
  liveAgents: readonly string[],
  resolveAgentId: (codeName: string) => string | undefined,
): DrainReleaseTargets {
  const targets: DrainReleaseTarget[] = [];
  const unresolved: string[] = [];
  for (const codeName of liveAgents) {
    const agentId = resolveAgentId(codeName);
    if (agentId) targets.push({ codeName, agentId });
    else unresolved.push(codeName);
  }
  return { targets, unresolved };
}

/**
 * The `scanned` value for the release step.
 *
 * This is the AC3 distinction as a function rather than a comment, so it can be
 * mutated in a test. `'not-scanned'` is NOT the same as `0`:
 *
 *   - `0`             we enumerated the live sessions and there were none
 *   - `'not-scanned'` we could not enumerate them, so we know nothing
 *
 * A drain that cannot look must never render as a drain that looked and found
 * nothing. That conflation is the whole bug.
 */
export function drainReleaseScanned(
  enumerated: boolean,
  liveAgentCount: number,
): number | 'not-scanned' {
  return enumerated ? liveAgentCount : 'not-scanned';
}

/** Per-agent outcome of the release POST, for the drain line's counters. */
export interface DrainReleaseOutcome {
  /** Cards actually moved out of `in_progress`. */
  released: number;
  /** Agents whose POST rejected or timed out. */
  failed: number;
}

/**
 * Fold the settled POST results into counters.
 *
 * A rejected POST counts as `failed`, never as "released 0". The drain runs
 * under a 15s backstop and the API can be slow or down; reporting an
 * unanswered call as a clean zero would reintroduce the false all-clear.
 */
export function tallyDrainReleases(
  results: readonly PromiseSettledResult<
    { released?: number; failed?: number; error?: unknown } | undefined
  >[],
): DrainReleaseOutcome {
  let released = 0;
  let failed = 0;
  for (const r of results) {
    if (r.status !== 'fulfilled') {
      failed += 1;
      continue;
    }
    // A fulfilled response carrying `error` also counts as failed. Today
    // `api.post` throws an ApiError on any non-2xx (`handleResponse`,
    // api-client.ts), so this branch is unreachable through that client and is
    // deliberately belt-and-braces: if the client is ever changed to return
    // bodies instead of throwing, the alternative is a route failure rendering
    // as `scanned=N acted=0` with no note — indistinguishable from "N agents,
    // none holding cards". That is precisely the false all-clear this whole
    // ticket is about, and it would reappear one layer down, silently.
    if (r.value && r.value.error !== undefined && r.value.error !== null) {
      failed += 1;
      continue;
    }
    const n = r.value?.released;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) released += n;
    // ENG-10367 (review): a PARTIAL failure. The route returns 200 with a
    // per-card `failed` count when some updates errored and others landed,
    // precisely so the successful releases are not lost to an exception — a
    // non-2xx would make `api.post` throw and take `released` down with it.
    // Without this the drain logged `acted=2` for a request that also left a
    // card in_progress, which is the same class of false all-clear as
    // reporting zero for a target we never heard from.
    const f = r.value?.failed;
    if (typeof f === 'number' && Number.isFinite(f) && f > 0) failed += f;
  }
  return { released, failed };
}
